import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Money, useToast } from '@clari/ui'
import { AccountHealthBadge } from '@/components/banking/AccountHealthBadge'
import { TransactionList } from '@/components/banking/TransactionList'
import { DashboardAccountSkeleton, TransactionListSkeleton } from '@/components/banking/skeletons'
import { authApi } from '@/lib/auth-api'
import { bankingApi } from '@/lib/banking-api'
import { useAuthStore } from '@/stores/authStore'

export function Dashboard() {
  const { t, i18n } = useTranslation(['auth', 'banking'])
  const navigate = useNavigate()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [resending, setResending] = useState(false)

  async function resendVerification() {
    if (resending) return
    setResending(true)
    try {
      await authApi.resendVerification()
      toast({ title: t('dashboard.verify_resent', { ns: 'auth' }), variant: 'success' })
    } catch {
      toast({ title: t('dashboard.verify_resend_error', { ns: 'auth' }), variant: 'error' })
    } finally {
      setResending(false)
    }
  }

  useEffect(() => {
    let active = true
    authApi
      .me()
      .then((r) => {
        if (active) setUser(r.user)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [setUser])

  // Swan onboarding is independent of Sumsub — gate on a verified email only.
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => bankingApi.listAccounts(),
    enabled: Boolean(user?.email_verified),
  })
  const account = accountsQuery.data?.items[0]
  // Created at finalize before its IBAN is provisioned (async) → "opening".
  const accountOpening = account ? account.status === 'opening' || !account.iban : false

  const txQuery = useQuery({
    queryKey: ['dashboard-tx', account?.id],
    queryFn: () => bankingApi.listTransactions(account?.id ?? '', { page: 1 }),
    enabled: Boolean(account),
  })

  const showVerify = user ? !user.email_verified : false
  const showKycStart = user ? user.email_verified && user.kyc_status === 'not_started' : false

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <h1 className="text-2xl font-semibold">
        {user?.first_name
          ? t('dashboard.welcome', { ns: 'auth', name: user.first_name })
          : t('dashboard.welcome_no_name', { ns: 'auth' })}
      </h1>

      {showVerify ? (
        <Card>
          <p className="text-text-muted">{t('dashboard.verify_banner', { ns: 'auth' })}</p>
          <Button
            className="mt-4"
            variant="secondary"
            disabled={resending}
            onClick={() => void resendVerification()}
          >
            {resending
              ? t('dashboard.verify_resending', { ns: 'auth' })
              : t('dashboard.verify_cta', { ns: 'auth' })}
          </Button>
        </Card>
      ) : null}

      {showKycStart ? (
        <Card>
          <p className="text-text-muted">{t('dashboard.kyc_banner', { ns: 'auth' })}</p>
          <Button className="mt-4" onClick={() => navigate('/app/onboarding/kyc')}>
            {t('dashboard.kyc_cta', { ns: 'auth' })}
          </Button>
        </Card>
      ) : null}

      {user?.email_verified && !account && !accountsQuery.isLoading ? (
        <Card>
          <h2 className="text-lg font-medium">{t('onboarding.open_account_title', { ns: 'banking' })}</h2>
          <p className="mt-2 text-text-muted">{t('onboarding.open_account_body', { ns: 'banking' })}</p>
          <Button
            className="mt-4"
            onClick={() => navigate('/app/onboarding/account')}
            withSignatureDot
          >
            {t('onboarding.open_account_cta', { ns: 'banking' })}
          </Button>
        </Card>
      ) : null}

      {account && accountOpening ? (
        <Card>
          <h2 className="text-lg font-medium">
            {t('onboarding.account_opening_title', { ns: 'banking' })}
          </h2>
          <p className="mt-2 text-text-muted">
            {t('onboarding.account_opening_body', { ns: 'banking' })}
          </p>
        </Card>
      ) : null}

      {user?.email_verified && !account && accountsQuery.isLoading ? (
        <DashboardAccountSkeleton />
      ) : null}

      {account && !accountOpening ? (
        <>
          <Card>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-text-muted">
                  {t('dashboard.available_balance', { ns: 'banking' })}
                </p>
                <Money
                  className="text-2xl"
                  amountCents={account.available_balance_cents}
                  currency={account.currency}
                  locale={i18n.language}
                />
              </div>
              <Link to="/app/account/health">
                <AccountHealthBadge indicator="green" />
              </Link>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => navigate('/app/transfer')} withSignatureDot>
                {t('dashboard.send_money', { ns: 'banking' })}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/app/cards')}>
                {t('nav.cards', { ns: 'banking' })}
              </Button>
            </div>
          </Card>

          <Card>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('dashboard.recent_transactions', { ns: 'banking' })}
              </h2>
              <Link to="/app/transactions" className="text-sm text-text-muted hover:text-text">
                {t('dashboard.see_all', { ns: 'banking' })}
              </Link>
            </div>
            {txQuery.isLoading ? (
              <TransactionListSkeleton rows={5} />
            ) : (
              <TransactionList
                items={(txQuery.data?.items ?? []).slice(0, 5)}
                locale={i18n.language}
              />
            )}
          </Card>
        </>
      ) : null}
    </div>
  )
}
